/**
 * Runway: how many days of approved contacts a campaign has left.
 *
 * The numbers themselves are computed once, on the backend, and shipped on the
 * home-state payload — Home and the sidebar both read them rather than deriving
 * their own, which is how they used to disagree ("0 days left" in the menu
 * beside a banner reading "3 days").
 *
 * What lives here is the small amount both sides still need to agree on: the
 * assumed pace behind the calculation, and the thresholds at which a low
 * reading becomes a warning. Declared in one package because a threshold that
 * exists twice is a threshold that will eventually differ, and the failure is
 * silent — Home warns at one point, the sidebar at another.
 */
/**
 * Contacts a day a campaign is assumed to send when no per-startup cap has been
 * recorded — which today is every startup: `daily_send_cap` is written only by
 * the admin "limits set" action, and the studio's button posts no number.
 *
 * A fallback rather than a column default or a backfill, deliberately. Stored
 * defaults go stale the day the real pace changes and have to be migrated
 * again; a constant is one edit, and an admin-set cap still overrides it per
 * startup.
 *
 * Why not measure the pace from what actually went out: a campaign cannot send
 * faster than its founder approves, so few approvals produce few sends, which
 * read as a slow pace, which makes a thin queue look like weeks of runway — the
 * low-runway warning switched itself off exactly when it was needed. Observed
 * sending is still used when it EXCEEDS this, since a campaign demonstrably
 * going faster is the better evidence about itself.
 */
export const DEFAULT_DAILY_SEND_CAP = 8;
/** Below this many days of runway, the warning appears. */
export const RUNWAY_ENTER_DAYS = 5;
/**
 * And only clears back above this many. The gap is hysteresis: without it a
 * queue hovering on the line flickers the badge on and off as each contact
 * sends.
 */
export const RUNWAY_EXIT_DAYS = 9;
/**
 * Whether a runway reading is worth warning about, given whether it was already
 * warning. Pass the previous answer back in so the hysteresis holds.
 */
export function shouldShowRunway(days, wasShowing) {
    if (days === null)
        return false;
    return wasShowing ? days <= RUNWAY_EXIT_DAYS : days < RUNWAY_ENTER_DAYS;
}
/**
 * Contacts a day a campaign should be assumed to send.
 *
 * An admin-set cap is the campaign's real limit and wins outright. Otherwise
 * the assumed pace applies, unless the campaign is demonstrably sending faster
 * than that — a campaign going faster is the better evidence about itself.
 *
 * Both the Home resolver and the sidebar call this. They used to each have
 * their own version, and the sidebar's had no cap in it at all, so the two
 * produced different runways for the same campaign.
 */
export function sendRatePerDay(input) {
    if (input.dailySendCap && input.dailySendCap > 0)
        return input.dailySendCap;
    return Math.max(DEFAULT_DAILY_SEND_CAP, input.recentDailySendRate ?? 0);
}
/**
 * Days of approved contacts one campaign has left.
 *
 * Sending capacity belongs to the LinkedIn account rather than to a campaign:
 * both campaigns push through the same sender, so they draw on one allowance.
 * Measure it once and split it across the campaigns actually running, or two
 * enabled campaigns each assume the whole budget and both report roughly double
 * the runway they have.
 */
export function trackRunwayDays(input) {
    return input.approved / (input.ratePerDay / Math.max(1, input.runningTracks));
}
//# sourceMappingURL=runway.js.map